import { com, kotlin } from '@eidu/web'
import React, { useEffect, useState } from 'react'
import PathField, { RenderablePath } from '../../pages/permission/path/PathField'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityType = com.eidu.entity.type.EntityType
import EntityPath = com.eidu.entity.path.EntityPath
import Field = com.eidu.entity.field.Field
import FieldType = com.eidu.entity.field.FieldType
import EdgeDirection = com.eidu.entity.path.EdgeDirection
import Edge = com.eidu.entity.path.Edge
import KtList = kotlin.collections.KtList
import FieldSingleSelect from './FieldSingleSelect'

type ComplexPathFieldProps = {
  entityTypeId: EntityTypeId
  targetType: EntityType | undefined
  selectedPath: EntityPath | undefined
  setSelectedPath: (path: EntityPath | undefined) => void
  renderablePaths: readonly RenderablePath[]
  fieldType: FieldType
  disabled?: boolean
}

const ComplexPathField = ({
  entityTypeId,
  targetType,
  selectedPath,
  setSelectedPath,
  renderablePaths,
  fieldType,
  disabled = false,
}: ComplexPathFieldProps) => {
  const [internalSelectedPath, setInternalSelectedPath] = useState<EntityPath>()
  const [selectedField, setSelectedField] = useState<Field>()

  useEffect(() => {
    setInternalSelectedPath(undefined)
    setSelectedField(undefined)

    if (!selectedPath || !targetType) return

    // Check if the selectedPath has a field at the end
    const edges = selectedPath.edges.asJsReadonlyArrayView()

    if (
      edges.length > 0 &&
      fieldType !== FieldType.Reference &&
      edges[edges.length - 1].nextType.equals(targetType.id)
    ) {
      // Extract the last edge (field) and the rest of the path
      const lastEdge = edges[edges.length - 1]

      // Find the field in the target type
      const field = targetType.fields.asJsReadonlyArrayView().find((f) => f.id.equals(lastEdge.field))

      if (field) {
        setSelectedField(field)

        if (edges.length > 1) {
          // Create a new path without the last edge
          const pathEdges = edges.slice(0, -1)
          const pathWithoutField = selectedPath.copy(
            selectedPath.fromType,
            lastEdge.nextType,
            KtList.fromJsArray(pathEdges)
          )
          setInternalSelectedPath(pathWithoutField)
        }
      } else {
        // If we can't find the field, just use the whole path
        setInternalSelectedPath(selectedPath)
      }
    } else {
      // No field at the end, just use the whole path
      setInternalSelectedPath(selectedPath)
    }
  }, [selectedPath, targetType, fieldType])

  useEffect(() => {
    let finalPath: EntityPath | undefined

    if (targetType && targetType.id.equals(entityTypeId) && !internalSelectedPath && selectedField) {
      // Case 1: Direct field on the entity type
      const edge = new Edge(EdgeDirection.Outgoing, selectedField.id, entityTypeId)
      finalPath = new EntityPath(entityTypeId, targetType.id, KtList.fromJsArray([edge]))
    }
    // Case 2: Normal case with a selected path
    else if (internalSelectedPath && targetType) {
      if (selectedField && fieldType === selectedField.type) {
        const edges = internalSelectedPath.edges.asJsReadonlyArrayView()
        const newFieldEdge = new Edge(EdgeDirection.Outgoing, selectedField.id, targetType.id)
        const newEdges = [...edges, newFieldEdge]
        finalPath = internalSelectedPath.copy(
          internalSelectedPath.fromType,
          targetType.id,
          KtList.fromJsArray(newEdges)
        )
      } else if (fieldType === FieldType.Reference) {
        finalPath = internalSelectedPath
      }
    }

    if (finalPath !== selectedPath) {
      setSelectedPath(finalPath)
    }
  }, [internalSelectedPath, selectedField, targetType, entityTypeId, fieldType, selectedPath])

  return (
    <>
      {targetType && !targetType.id.equals(entityTypeId) && (
        <PathField
          selectedPath={internalSelectedPath}
          paths={renderablePaths}
          onChange={(path) => {
            setInternalSelectedPath(path)
            setSelectedField(undefined)
          }}
          disabled={disabled}
        />
      )}
      {targetType &&
        (targetType.id.equals(entityTypeId) || internalSelectedPath) &&
        fieldType !== FieldType.Reference && (
          <FieldSingleSelect
            label="Target field"
            options={targetType.fields.asJsReadonlyArrayView().filter(({ type }) => type === fieldType)}
            selected={selectedField}
            setSelected={setSelectedField}
            entityType={targetType}
            errorLabel={`Requires selecting a field of type '${fieldType}' but not found on type '${targetType.name}'.`}
            disabled={disabled}
          />
        )}
    </>
  )
}

export default ComplexPathField
